<template>
  <div class="documentation-container">
    <div class="container py-5">
      <h1 class="text-center mb-5">KDIY Order App Documentation</h1>
      
      <div class="row">
        <div class="col-md-3">
          <!-- Sidebar Navigation -->
          <div class="list-group mb-4">
            <button 
              v-for="(section, index) in sections" 
              :key="index"
              class="list-group-item list-group-item-action"
              :class="{ active: activeSection === index }"
              @click="activeSection = index"
            >
              {{ section.title }}
            </button>
          </div>
        </div>
        
        <div class="col-md-9">
          <!-- Documentation Content -->
          <div class="card">
            <div class="card-header bg-primary text-white">
              <h2 class="mb-0">{{ sections[activeSection].title }}</h2>
            </div>
            <div class="card-body">
              <div v-if="activeSection === 0">
                <!-- Project Overview -->
                <h3>Project Overview</h3>
                <p>The KDIY Order App is a Vue.js-based web application designed to facilitate seamless order placement and payment processing for KDIY customers. The application provides an intuitive interface for users to place orders, specify quantities, and complete payments through various payment methods.</p>
                
                <h4>Core Purpose</h4>
                <p>The primary purpose of the KDIY Order App is to streamline the order and payment process for KDIY products and services. It serves as a bridge between customers and the KDIY payment processing system, allowing for efficient transaction handling.</p>
                
                <h4>Main Features</h4>
                <ul>
                  <li><strong>User-Friendly Gateway:</strong> A welcoming landing page that directs users to the payment interface.</li>
                  <li><strong>Dynamic Payment Form:</strong> A comprehensive form that adapts to different payment scenarios.</li>
                  <li><strong>Regional Support:</strong> Integration with different depots across regions.</li>
                  <li><strong>Error Handling:</strong> Robust error management with user-friendly notifications.</li>
                </ul>
              </div>
              
              <div v-if="activeSection === 1">
                <!-- Technology Stack -->
                <h3>Technology Stack</h3>
                <h4>Frontend Framework</h4>
                <p>The application is built using Vue.js 2.6.14 with the following key components:</p>
                <ul>
                  <li><strong>Vue Router 3.5.1:</strong> For application routing and navigation</li>
                  <li><strong>Vuex 3.6.2:</strong> For centralized state management</li>
                  <li><strong>Bootstrap 5.3.3:</strong> For responsive UI components and styling</li>
                  <li><strong>Axios 1.1.3:</strong> For HTTP requests to backend services</li>
                </ul>
                
                <h4>Additional Libraries</h4>
                <ul>
                  <li><strong>SweetAlert2:</strong> For enhanced user notifications</li>
                  <li><strong>Vue-select:</strong> For enhanced dropdown components</li>
                  <li><strong>FontAwesome:</strong> For icons</li>
                </ul>
              </div>
              
              <div v-if="activeSection === 2">
                <!-- Core Features -->
                <h3>Core Features</h3>
                
                <div class="feature-section mb-4">
                  <h4>Gateway</h4>
                  <p>The Gateway feature serves as the entry point to the application, providing a welcoming landing page that introduces users to the application and directs them to the payment interface.</p>
                  <p><strong>Key Components:</strong> GateWayView.vue</p>
                </div>
                
                <div class="feature-section mb-4">
                  <h4>Payment Processing</h4>
                  <p>The Payment Processing feature is the core functionality of the application. It provides a comprehensive form that allows users to enter payment details, select quantities, configure installment payments, and complete their orders.</p>
                  <p><strong>Key Components:</strong> PaymentView.vue</p>
                </div>
                
                <div class="feature-section mb-4">
                  <h4>Regional Support</h4>
                  <p>The Regional Support feature enables the application to handle different geographical regions and their associated depots. This feature allows users to select and interact with location-specific data.</p>
                  <p><strong>Key Components:</strong> Integrated into PaymentView.vue</p>
                </div>
                
                <div class="feature-section">
                  <h4>Error Handling</h4>
                  <p>The Error Handling feature provides a robust system for catching, processing, and displaying errors to users in a friendly manner.</p>
                  <p><strong>Key Components:</strong> errorHandler.js, responses.js</p>
                </div>
              </div>
              
              <div v-if="activeSection === 3">
                <!-- Architecture -->
                <h3>Architecture</h3>
                <p>The KDIY Order App follows a component-based architecture using Vue.js 2 as its primary framework.</p>
                
                <h4>Application Structure</h4>
                <ul>
                  <li><strong>Entry Point:</strong> main.js initializes the Vue application</li>
                  <li><strong>App Shell:</strong> App.vue provides the application shell with router-view</li>
                  <li><strong>Routing:</strong> Vue Router handles navigation between views</li>
                  <li><strong>State Management:</strong> Vuex store manages application state</li>
                  <li><strong>Services:</strong> Utility services handle cross-cutting concerns</li>
                  <li><strong>Views:</strong> Top-level components corresponding to routes</li>
                </ul>
                
                <h4>Data Flow</h4>
                <p>The application follows a unidirectional data flow pattern:</p>
                <ol>
                  <li>User interacts with components</li>
                  <li>Components dispatch actions to the Vuex store</li>
                  <li>Actions perform API calls through services</li>
                  <li>API responses trigger mutations to update state</li>
                  <li>Components reactively update based on state changes</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectDocumentationView',
  data() {
    return {
      activeSection: 0,
      sections: [
        { title: 'Project Overview' },
        { title: 'Technology Stack' },
        { title: 'Core Features' },
        { title: 'Architecture' }
      ]
    };
  }
};
</script>

<style scoped>
.documentation-container {
  background-color: #f8f9fa;
  min-height: 100vh;
}

.list-group-item.active {
  background-color: #007bff;
  border-color: #007bff;
}

.card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.feature-section {
  border-left: 4px solid #007bff;
  padding-left: 1rem;
}

h3 {
  color: #007bff;
  margin-bottom: 1.5rem;
}

h4 {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  color: #495057;
}
</style>